import {
  isDateLike,
  isDayjs,
  isMoment,
  isNotEmpty,
  isValidDate
} from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import { SchemaTransformError } from '../../errors'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type { SchemaDateTimeSpecific } from '../../types'

export const dateHelper = {
  DATE_ONLY_ISO_REGEX: /^\d{4}-\d{2}-\d{2}$/,
  FULL_ISO_REGEX:
    /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|([+-]\d{2}:?\d{2}))?)?$/,
  LOOSE_ISO_REGEX:
    /(^\d{4}(?:-\d{2}){2})+(?:[ _t](\d{1,2}(?::\d{2}){1,2}(?:\.\d+)?))? ?(z|[+-]\d{2}:?\d{2})?/i,
  tryFormatToISOString: (value: string) => {
    const match = dateHelper.LOOSE_ISO_REGEX.exec(value)
    if (!match) return null
    const [, date, time, offset] = match
    let normalizedOffset = ''
    if (offset) {
      normalizedOffset =
        offset.toUpperCase() === 'Z'
          ? 'Z'
          : `${offset.slice(0, 3)}:${offset.slice(-2)}`
    }
    // 如果 HH 只有一位，补0
    if (/^\d:/.test(time)) {
      return `${date}${time ? `T0${time}` : ''}${normalizedOffset}`
    }
    return `${date}${time ? `T${time}` : ''}${normalizedOffset}`
  }
}

export class SchemaDateTimeDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaDateTimeSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaDateTimeSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaDateTimeSpecific
  ): SchemaDateTimeSpecific {
    return { ...schema, type: 'Schema.DateTime', value: target }
  }

  override inferOnlySchema(schema: SchemaDateTimeSpecific) {
    return schema
  }

  override inferTypeScript(schema: SchemaDateTimeSpecific): string {
    const value = schema.value
    if (value !== undefined && this.test(value, schema))
      return JSON.stringify(value)
    return 'string'
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaDateTimeSpecific,
    path: PropertyKey[]
  ) {
    if (target === null && schema.nullable) return target
    return this.transformToDateTimeString(target, schema, path)
  }

  transformToDateTimeString(
    value: any,
    schema: SchemaDateTimeSpecific,
    path: PropertyKey[]
  ) {
    if (isValidDate(value)) {
      return dateFormat(value)
    }
    if (isMoment(value) || isDayjs(value)) {
      if ('toDate' in value) {
        return dateFormat(value.toDate())
      }
      return dateFormat(new Date(value))
    }
    if (typeof value === 'number') {
      const date = new Date(value)
      if (isValidDate(date)) {
        return dateFormat(date)
      }
    }
    if (typeof value === 'string') {
      if (/^(19|20)\d\d(0\d|1[0-2])([0-2]\d|3[01])$/.test(value)) {
        const year = value.slice(0, 4)
        const month = value.slice(4, 6)
        const day = value.slice(6, 8)
        const date = new Date(Number(year), Number(month) - 1, Number(day))

        if (isValidDate(date)) {
          return `${year}-${month}-${day}T00:00:00.000Z`
        }
      } else if (dateHelper.FULL_ISO_REGEX.test(value)) {
        return value
      }
      const mayISO = dateHelper.tryFormatToISOString(value)
      if (mayISO) {
        return mayISO
      }
      if (value) {
        const date = new Date(value)
        if (isValidDate(date)) {
          return dateFormat(date)
        } else if (value !== schema.fallback) {
          this.raise(new SchemaTransformError(schema.name))
          return
        }
      }

      return super.transform(value, { type: 'Schema.String' }, path)
    }
    if (value !== schema.fallback) {
      this.raise(new SchemaTransformError(schema.name))
    }
    return ''
  }

  override validate(target: any, schema: SchemaDateTimeSpecific) {
    this.validateMetadata(target, schema, isDateLike, isNotEmpty)
  }
}

export function dateFormat(date: Date) {
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1, 2)
  const day = pad(date.getDate(), 2)
  const hours = pad(date.getHours(), 2)
  const minutes = pad(date.getMinutes(), 2)
  const seconds = pad(date.getSeconds(), 2)
  const milliseconds = pad(date.getMilliseconds(), 3)
  const timezoneOffset = getTimezoneOffset(date.getTimezoneOffset())

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezoneOffset}`
}

function getTimezoneOffset(offset: number) {
  const sign = offset <= 0 ? '+' : '-'
  const absOffset = Math.abs(offset)
  const hours = pad(Math.floor(absOffset / 60), 2)
  const minutes = pad(absOffset % 60, 2)
  return `${sign}${hours}${minutes}`
}

function pad(number: number, length: number) {
  return String(number).padStart(length, '0')
}
